/**
 * useBuildLogs — fetches build/deploy logs for a system's services.
 *
 * For rusha_managed builds: subscribes to WebSocket log events via
 * useDeploymentStream's logBatches, falling back to REST polling.
 * For CI-mode (github-actions) builds: fetches GitHub Actions job logs
 * via the REST endpoint GET /api/v1/applications/:alias/build-logs.
 *
 * Options:
 *  - autoFetch: fetch on mount (default true)
 *  - pollWhileActive: poll every 20s while build is active (CI-mode only)
 *  - logBatches: live log batches from useDeploymentStream (rusha_managed mode)
 *  - buildMode: "rusha_managed" | "ci" — determines log source
 */

import { useState, useEffect, useCallback, useRef } from "react";

import { ApiError } from "@platform/http/shared-http";

import applicationApi from "../module/api/applicationApi";
import type {
  BuildLogJob,
  BuildLogsResult,
} from "../module/api/IApplicationApi";

export interface UseBuildLogsResult {
  logs: BuildLogJob[];
  /** Live log lines from rusha_managed builds, keyed by service → phase → lines */
  liveLogs: Map<string, Map<string, string[]>>;
  /** CI quality gate jobs from the provisioned ci.yml (rusha_managed only) */
  qualityGates: BuildLogJob[];
  /** Identifies the log source so the UI can render appropriately */
  source: "rusha_managed" | "github_actions" | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Pipeline run metadata — populated for rusha_managed builds */
  runId?: number | string;
  runStatus?: string;
  runConclusion?: string | null;
  runHtmlUrl?: string;
}

/** Build a dedupe key for a job: jobId uniquely identifies a GH Actions job run. */
function jobKey(job: BuildLogJob): string {
  return `${job.jobId}`;
}

/** Minimum time to wait after a 429 before retrying (ms). */
const RATE_LIMIT_BACKOFF_MS = 30_000;

export function useBuildLogs(
  alias: string,
  serviceName?: string,
  options?: {
    autoFetch?: boolean;
    pollWhileActive?: boolean;
    logBatches?: Map<string, Map<string, string[]>>;
    buildMode?: "rusha_managed" | "ci";
    /** When set, fetch logs for this specific sagaId instead of the latest */
    sagaId?: string;
  },
): UseBuildLogsResult {
  const [logs, setLogs] = useState<BuildLogJob[]>([]);
  const [qualityGates, setQualityGates] = useState<BuildLogJob[]>([]);
  const [source, setSource] = useState<
    "rusha_managed" | "github_actions" | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pipeline run metadata for CI run details
  const [runId, setRunId] = useState<number | string | undefined>();
  const [runStatus, setRunStatus] = useState<string | undefined>();
  const [runConclusion, setRunConclusion] = useState<
    string | null | undefined
  >();
  const [runHtmlUrl, setRunHtmlUrl] = useState<string | undefined>();
  const pollWhileActiveRef = useRef(options?.pollWhileActive);
  const isRushaManaged = options?.buildMode === "rusha_managed";

  // Cache for incremental merges — keyed by alias+sagaId+serviceName to invalidate on switch
  const cacheRef = useRef<Map<string, BuildLogJob>>(new Map());
  const cacheKeyRef = useRef(
    `${alias}:${options?.sagaId ?? ""}:${serviceName ?? ""}`,
  );
  const lastFetchTimeRef = useRef<number>(0);

  // Track rate-limit backoff: skip fetches until this timestamp passes
  const rateLimitedUntilRef = useRef<number>(0);

  // Keep ref in sync so the interval callback always has the latest value
  useEffect(() => {
    pollWhileActiveRef.current = options?.pollWhileActive;
  }, [options?.pollWhileActive]);

  // Invalidate cache when alias or serviceName changes
  useEffect(() => {
    const newKey = `${alias}:${options?.sagaId ?? ""}:${serviceName ?? ""}`;
    if (newKey !== cacheKeyRef.current) {
      cacheKeyRef.current = newKey;
      cacheRef.current.clear();
      lastFetchTimeRef.current = 0;
      rateLimitedUntilRef.current = 0;
    }
  }, [alias, serviceName, options?.sagaId]);

  const fetchLogs = useCallback(async () => {
    if (!alias) return;

    // Skip if we're in a rate-limit backoff window
    if (Date.now() < rateLimitedUntilRef.current) return;

    setLoading(true);
    setError(null);
    try {
      const result: BuildLogsResult = await applicationApi.getBuildLogs(
        alias,
        serviceName,
        options?.sagaId ? { sagaId: options.sagaId } : undefined,
      );

      // Merge into cache: keep existing entries that haven't changed,
      // add/update entries from the new response.
      const newCache = new Map<string, BuildLogJob>();
      for (const job of result.logs) {
        const key = jobKey(job);
        const cached = cacheRef.current.get(key);
        // Reuse cached entry if conclusion and status haven't changed and log
        // lines haven't grown — avoids unnecessary re-renders.
        if (
          cached &&
          cached.conclusion === job.conclusion &&
          cached.status === job.status &&
          cached.logLines.length === job.logLines.length
        ) {
          newCache.set(key, cached);
        } else {
          newCache.set(key, job);
        }
      }

      cacheRef.current = newCache;
      lastFetchTimeRef.current = Date.now();
      setLogs(Array.from(newCache.values()));

      // Store quality gates and source from the response
      if (result.qualityGates) {
        setQualityGates(result.qualityGates);
      }
      if (result.source) {
        setSource(result.source);
      }

      if (result.message && result.logs.length === 0) {
        setError(result.message);
      }
    } catch (err) {
      // On 429 rate limit, back off before retrying
      if (err instanceof ApiError && err.status === 429) {
        const backoffMs = err.retryAfter
          ? err.retryAfter * 1000
          : RATE_LIMIT_BACKOFF_MS;
        rateLimitedUntilRef.current = Date.now() + backoffMs;
        setError("Rate limited. Will retry shortly.");
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to fetch build logs",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [alias, serviceName, options?.sagaId]);

  useEffect(() => {
    if (options?.autoFetch !== false && !isRushaManaged) {
      void fetchLogs();
    }
  }, [fetchLogs, options?.autoFetch, isRushaManaged]);

  // Poll every 20s while build is active (CI-mode only)
  useEffect(() => {
    if (!options?.pollWhileActive || isRushaManaged) return;
    const id = setInterval(() => {
      if (pollWhileActiveRef.current) {
        void fetchLogs();
      }
    }, 20_000);
    return () => clearInterval(id);
  }, [fetchLogs, options?.pollWhileActive, isRushaManaged]);

  // For rusha_managed mode, fetch logs from REST endpoint initially,
  // then rely on WebSocket live updates from useDeploymentStream.
  // Unlike the CI-mode fetch above, this ALWAYS fires on mount regardless
  // of autoFetch — users expanding a completed build node expect to see
  // historical logs immediately.
  useEffect(() => {
    if (!isRushaManaged || !alias) return;

    const fetchManagedLogs = async () => {
      setLoading(true);
      try {
        const res = await applicationApi.getBuildLogs(
          alias,
          serviceName,
          options?.sagaId ? { sagaId: options.sagaId } : undefined,
        );
        // For rusha_managed, logs come from the BuildLog table (Kaniko output)
        // and qualityGates come from the provisioned ci.yml workflow
        const newCache = new Map<string, BuildLogJob>();
        for (const job of res.logs) {
          newCache.set(jobKey(job), job);
        }
        cacheRef.current = newCache;
        setLogs(Array.from(newCache.values()));
        if (res.qualityGates) {
          setQualityGates(res.qualityGates);
        }
        if (res.source) {
          setSource(res.source);
        }
        // Store pipeline run metadata for CI run details
        if (res.runId) {
          setRunId(res.runId);
        }
        if (res.runStatus) {
          setRunStatus(res.runStatus);
        }
        if (res.runConclusion) {
          setRunConclusion(res.runConclusion);
        }
        if (res.htmlUrl) {
          setRunHtmlUrl(res.htmlUrl);
        }
      } catch (err) {
        // Surface errors so the user knows logs are unavailable rather
        // than seeing a blank panel with no explanation.
        setError(
          err instanceof Error ? err.message : "Failed to fetch build logs",
        );
      } finally {
        setLoading(false);
      }
    };

    void fetchManagedLogs();
  }, [alias, serviceName, isRushaManaged, options?.sagaId]);

  return {
    logs,
    liveLogs: options?.logBatches ?? new Map(),
    qualityGates,
    source,
    loading,
    error,
    refetch: fetchLogs,
    runId,
    runStatus,
    runConclusion,
    runHtmlUrl,
  };
}
